/**
 * Vehicles: response schemas (lenient) and pure mappers to the domain.
 * Endpoints: /api/client/available-cars, /api/client/bikes/available, /api/client/cars/:id,
 * /api/client/bikes/:id, /api/client/cars-by-city, /api/client/bikes-by-city, /api/categories,
 * /api/client/agencies/:id, /api/agency-rules/public/:agencyId, /api/wishlist.
 */
import { z } from 'zod';
import { getT, ns, translate, type PlainKeyOf } from '@/i18n';
import type {
  AgencySummary,
  CategorySummary,
  City,
  Destination,
  IncludedItem,
  Vehicle,
  VehicleKind,
  VehiclePriceTier,
  VehicleSummary,
} from '../../domain';
import type { PriceTier, PricingVehicle } from '@/lib/pricing';
import { destinationPhoto, cityKey } from './destinationPhotos';
import { firstImage, assetUrl, VEHICLE_PLACEHOLDER } from './media';

/* ── Schemas ────────────────────────────────────────────────────────────── */

const n = z.number().nullish();
const s = z.string().nullish();
const b = z.boolean().nullish();
const anyId = z.union([z.string(), z.object({ _id: z.string() }).passthrough()]).nullish();

export const TierSchema = z.object({ days: z.number(), pricePerDay: z.number(), basePricePerDay: n }).passthrough();

const SubBrandSchema = z
  .object({ _id: s, name: s, slug: s, images: z.array(z.unknown()).nullish(), categoryName: s, categorySlug: s })
  .passthrough();
const BrandSchema = z.object({ _id: s, name: s, slug: s, images: z.array(z.unknown()).nullish() }).passthrough();

/** A car or a bike document as the client endpoints return it. */
export const VehicleRecordSchema = z
  .object({
    _id: z.string(),
    agencyId: anyId,
    city: s,
    displayCities: z.array(z.string()).nullish(),
    // cars: names; bikes: populated brand / sub-brand objects (or ObjectId strings on raw documents)
    brand: z.union([z.string(), BrandSchema]).nullish(),
    subBrand: z.union([z.string(), SubBrandSchema]).nullish(),
    name: s,
    category: s,
    catSlug: s,
    categoryId: anyId,
    // car fields
    transmission: s,
    seats: n,
    registrationYear: n,
    // bike fields
    gearType: s,
    engineCC: n,
    modelYear: n,
    permitRequired: s,
    fuelType: s,
    pricePerDay: n,
    basePricePerDay: n,
    pricePerDayDiscounted: n,
    pricingTiers: z.array(TierSchema).nullish(),
    deposit: n,
    images: z.array(z.unknown()).nullish(),
    minRentalDays: n,
    status: s,
    description: s,
    averageRating: n,
    totalReviews: n,
    deliveryEnabled: b,
    deliveryPrice: n,
    freeDelivery: b,
    agencyMinDays: n,
    agencyRating: n,
    agencyTotalReviews: n,
    pricingSource: s,
    activeCampaignName: s,
    campaignStartDate: s,
    campaignEndDate: s,
    originalPricePerDay: n,
    availabilityConfirmedToday: b,
  })
  .passthrough();
export type VehicleRecord = z.infer<typeof VehicleRecordSchema>;

export const AvailableCarsSchema = z
  .object({
    cars: z.array(VehicleRecordSchema).default([]),
    totalPages: n,
    blackout: b,
    blackoutReason: s,
  })
  .passthrough();
export const AvailableBikesSchema = z
  .object({ bikes: z.array(VehicleRecordSchema).default([]), total: n, blackout: b })
  .passthrough();

export const CarsByCitySchema = z
  .object({
    citiesWithCars: z
      .array(z.object({ _id: z.string(), cars: z.array(VehicleRecordSchema).default([]) }).passthrough())
      .default([]),
  })
  .passthrough();
export const BikesByCitySchema = z
  .object({
    citiesWithBikes: z
      .array(
        z
          .object({
            _id: z.string(),
            bikes: z.array(z.object({ brand: z.unknown(), subBrand: z.unknown() }).passthrough()).default([]),
          })
          .passthrough(),
      )
      .default([]),
  })
  .passthrough();

export const CategoryListSchema = z.array(
  z.object({ _id: z.string(), name: z.string(), slug: s, type: s, image: s }).passthrough(),
);
export type CategoryRecord = z.infer<typeof CategoryListSchema>[number];

export const AvailabilitySchema = z
  .object({ available: z.unknown(), availableCount: n, totalUnits: n, blackout: b, blackoutReason: s })
  .passthrough();

export const PublicAgencySchema = z
  .object({
    _id: z.string(),
    agencyName: s,
    city: s,
    zone: s,
    logo: s,
    createdAt: s,
    rating: n,
    totalReviews: n,
    minimumRentalDays: n,
    deliveryEnabled: z.union([z.boolean(), z.string()]).nullish(),
    deliveryPrice: n,
    freeDelivery: b,
  })
  .passthrough();
export type PublicAgency = z.infer<typeof PublicAgencySchema>;

const RuleSchema = z.object({ title: s, description: s, value: z.unknown(), clientOn: s, clientOff: s }).passthrough();
export const AgencyRulesSchema = z.array(
  z.object({ isDefault: b, rules: z.array(RuleSchema).nullish() }).passthrough(),
);
export type AgencyRuleSet = z.infer<typeof AgencyRulesSchema>[number];

/* ── Labels ─────────────────────────────────────────────────────────────── */

type LabelKey = PlainKeyOf<typeof ns.labels>;
const TRANSMISSION: Record<string, LabelKey> = {
  manual: 'transmission.manual',
  automatic: 'transmission.automatic',
};
const FUEL: Record<string, LabelKey> = {
  diesel: 'fuel.diesel',
  essence: 'fuel.petrol',
  petrol: 'fuel.petrol',
  hybrid: 'fuel.hybrid',
  electric: 'fuel.electric',
};
/** A known server value in the current language; an unknown one as the server sent it. */
const known = (table: Record<string, LabelKey>, v: string | null | undefined) => {
  const key = v ? table[v.toLowerCase()] : undefined;
  return key ? translate(ns.labels, key) : (v ?? '');
};
export const transmissionLabel = (v: string | null | undefined) => known(TRANSMISSION, v);
export const fuelLabel = (v: string | null | undefined) => known(FUEL, v);

/** Domain transmission filter (French values, kept from phase 1: state and URLs) → backend values. */
export const TRANSMISSION_PARAM: Record<'Manuelle' | 'Automatique', string> = {
  Manuelle: 'Manual',
  Automatique: 'Automatic',
};

/* ── Mappers ────────────────────────────────────────────────────────────── */

/**
 * Car or bike, decided from the document itself (LOGIC.md F4/F9: the old site guessed from which
 * endpoint answered, and had two `engineCC` tests). Bikes always carry `engineCC`, `gearType` and
 * `modelYear` (required by the Bike schema); cars never do.
 */
export function vehicleKindOf(raw: Pick<VehicleRecord, 'engineCC' | 'gearType' | 'modelYear'>): VehicleKind {
  return typeof raw.engineCC === 'number' || typeof raw.gearType === 'string' || typeof raw.modelYear === 'number'
    ? 'moto'
    : 'car';
}

const refName = (v: VehicleRecord['brand']): string => (typeof v === 'string' ? v : (v?.name ?? ''));
const isObjectId = (v: string) => /^[a-f\d]{24}$/i.test(v);
export const idOf = (v: unknown): string => {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object' && '_id' in v) return String((v as { _id: unknown })._id ?? '');
  return '';
};

export function vehicleName(raw: VehicleRecord): string {
  const brand = refName(raw.brand);
  const model = refName(raw.subBrand);
  // Raw bike documents (wishlist) only hold ObjectIds: no readable name.
  const parts = [brand, model].filter((p) => p && !isObjectId(p));
  return parts.join(' ').trim() || raw.name || translate(ns.labels, 'vehicle');
}

export function vehicleCategory(raw: VehicleRecord, categories?: Map<string, string>): string {
  if (raw.category) return raw.category;
  const sub = typeof raw.subBrand === 'object' ? raw.subBrand : null;
  if (sub?.categoryName) return sub.categoryName;
  const byId = categories?.get(idOf(raw.categoryId)) ?? (raw.catSlug ? categories?.get(raw.catSlug) : undefined);
  return byId ?? '';
}

/** The lowest tier price from 2 days or more (bikes), with its threshold. */
export function tierFrom(tiers: PriceTier[] | null | undefined, pricePerDay: number) {
  const lower = (tiers ?? [])
    .filter((t) => t.days > 1 && t.pricePerDay > 0 && t.pricePerDay < pricePerDay)
    .sort((a, b) => a.days - b.days)[0];
  return lower ? { price: lower.pricePerDay, days: lower.days } : undefined;
}

/**
 * Every price step of a bike (PR-07), by ascending days, one per day count (the first wins, as the server's
 * `reduce` keeps the first of equal days). A step priced 0 costs the base price, as on the server (`|| base`).
 */
export function tiersOf(tiers: PriceTier[] | null | undefined, pricePerDay: number): VehiclePriceTier[] {
  const out: VehiclePriceTier[] = [];
  for (const t of tiers ?? []) {
    const fromDays = Number(t.days);
    if (!Number.isFinite(fromDays) || out.some((o) => o.fromDays === fromDays)) continue;
    out.push({ fromDays, pricePerDay: Number(t.pricePerDay) || pricePerDay });
  }
  return out.sort((a, b) => a.fromDays - b.fromDays);
}

export function vehicleImage(baseUrl: string, raw: VehicleRecord): string {
  const own = firstImage(baseUrl, raw.images);
  if (own) return own;
  const sub = typeof raw.subBrand === 'object' ? raw.subBrand : null;
  return firstImage(baseUrl, sub?.images) || VEHICLE_PLACEHOLDER;
}

export function toVehicleSummary(
  raw: VehicleRecord,
  baseUrl: string,
  categories?: Map<string, string>,
): VehicleSummary {
  const kind = vehicleKindOf(raw);
  const pricePerDay = Number(raw.pricePerDay) || 0;
  const tier = kind === 'moto' ? tierFrom(raw.pricingTiers, pricePerDay) : undefined;
  const year = kind === 'moto' ? raw.modelYear : raw.registrationYear;
  return {
    id: raw._id,
    name: vehicleName(raw),
    year: year ? String(year) : undefined,
    kind,
    category: vehicleCategory(raw, categories),
    transmission: transmissionLabel(kind === 'moto' ? raw.gearType : raw.transmission),
    fuel: fuelLabel(raw.fuelType),
    seats: kind === 'moto' ? 2 : Number(raw.seats) || 5,
    pricePerDay,
    tierPricePerDay: tier?.price,
    tierFromDays: tier?.days,
    tiers: kind === 'moto' && raw.pricingTiers?.length ? tiersOf(raw.pricingTiers, pricePerDay) : undefined,
    deposit: Number(raw.deposit) || 0,
    freeDelivery: raw.freeDelivery === true,
    image: vehicleImage(baseUrl, raw),
    tag: raw.availabilityConfirmedToday
      ? { label: translate(ns.labels, 'availabilityConfirmed'), tone: 'lime' }
      : undefined,
    city: raw.city ?? '',
  };
}

/** The fields the guest estimate needs (lib/pricing). */
export function toPricingVehicle(raw: VehicleRecord): PricingVehicle {
  const kind = vehicleKindOf(raw);
  return {
    type: kind === 'moto' ? 'bike' : 'car',
    pricePerDay: Number(raw.pricePerDay) || 0,
    basePricePerDay: Number(raw.basePricePerDay) || 0,
    pricingTiers: raw.pricingTiers ?? undefined,
    minRentalDays: raw.minRentalDays ?? null,
  };
}

/** «Au-delà de 200 km/jour : 2 MAD/km.» and the agency's own sentences (LOGIC.md PR-18). */
export function ruleStatements(sets: AgencyRuleSet[]): { mileage?: string; sentences: string[] } {
  const set = sets.find((r) => r.isDefault) ?? sets[0];
  const sentences: string[] = [];
  let mileage: string | undefined;
  for (const rule of set?.rules ?? []) {
    const v = rule.value;
    if (typeof v === 'boolean' || v === 'true' || v === 'false') {
      const on = v === true || v === 'true';
      const text = (on ? rule.clientOn : rule.clientOff)?.trim();
      if (text) sentences.push(text);
    } else if (v && typeof v === 'object' && 'thresholdPerDay' in v) {
      const t = Number((v as { thresholdPerDay: unknown }).thresholdPerDay);
      const p = Number((v as { pricePerKm?: unknown }).pricePerKm);
      if (Number.isFinite(t) && t > 0) {
        const tr = getT(ns.labels);
        mileage = tr('mileage.perDay', { km: t });
        sentences.push(
          Number.isFinite(p) && p > 0
            ? tr('mileage.extraCharged', { km: t, price: p })
            : tr('mileage.included', { km: t }),
        );
      }
      if (rule.clientOn?.trim()) sentences.push(rule.clientOn.trim());
    } else if (typeof v === 'string' && v.trim()) {
      sentences.push(v.trim());
    }
  }
  return { mileage, sentences };
}

export function toAgencySummary(agency: PublicAgency | null, fallbackCity: string): AgencySummary {
  const count = Number(agency?.totalReviews) || 0;
  const average = Number(agency?.rating) || 0;
  return {
    id: agency?._id ?? '',
    name: agency?.agencyName || translate(ns.labels, 'partnerAgency'),
    city: agency?.city || fallbackCity,
    // Only agencies the platform lets clients book (online payment activated, D13) list vehicles.
    verified: true,
    responseTime: '',
    rating: count > 0 && average > 0 ? { average, count } : undefined,
  };
}

export function toVehicle(
  raw: VehicleRecord,
  baseUrl: string,
  agency: PublicAgency | null,
  rules: AgencyRuleSet[],
  categories?: Map<string, string>,
): Vehicle {
  const summary = toVehicleSummary(raw, baseUrl, categories);
  const { mileage, sentences } = ruleStatements(rules);
  const deliveryOn = agency
    ? agency.deliveryEnabled === true || agency.deliveryEnabled === 'true'
    : raw.deliveryEnabled === true;
  const deliveryFree = agency
    ? agency.freeDelivery === true || Number(agency.deliveryPrice) === 0
    : summary.freeDelivery;
  const t = getT(ns.labels);
  const included: IncludedItem[] = [];
  if (mileage) included.push({ icon: 'road', title: mileage, text: t('included.mileage') });
  included.push({ icon: 'clock', title: t('included.cancel.title'), text: t('included.cancel.text') });
  included.push({ icon: 'headset', title: t('included.support.title'), text: t('included.support.text') });
  if (deliveryOn)
    included.push({
      icon: 'truck',
      title: t('included.delivery.title'),
      text: deliveryFree ? t('included.delivery.free') : t('included.delivery.paid'),
    });
  const count = Number(raw.totalReviews) || 0;
  const images = (raw.images ?? []).map((i) =>
    assetUrl(baseUrl, typeof i === 'string' ? i : (i as { url?: unknown })?.url),
  );
  return {
    ...summary,
    freeDelivery: deliveryOn && deliveryFree,
    gallery: images.filter(Boolean).slice(1),
    agency: toAgencySummary(agency, summary.city),
    mileage: mileage ?? t('mileage.unknown'),
    minDays: Math.max(1, Number(raw.minRentalDays) || 1),
    rating: count > 0 ? { average: Number(raw.averageRating) || 0, count } : undefined,
    included,
    rules: sentences,
    // No public endpoint lists the booked days of a model: the availability check covers it.
    unavailable: [],
  };
}

/** Categories of a kind with their count, cheapest price and an image, from the vehicles of a city. */
export function toCategorySummaries(
  categories: CategoryRecord[],
  vehicles: VehicleSummary[],
  baseUrl: string,
): CategorySummary[] {
  const out: CategorySummary[] = [];
  for (const cat of categories) {
    const inCat = vehicles.filter((v) => v.category === cat.name);
    if (!inCat.length) continue;
    const withPhoto = inCat.find((v) => v.image && v.image !== VEHICLE_PLACEHOLDER);
    out.push({
      category: cat.name,
      count: inCat.length,
      fromPrice: Math.min(...inCat.map((v) => v.pricePerDay)),
      image: withPhoto?.image ?? (assetUrl(baseUrl, cat.image, 'categories') || VEHICLE_PLACEHOLDER),
    });
  }
  return out;
}

/** IATA codes shown on the destination tiles (reference data, not from the API). */
export const AIRPORT_CODES: Record<string, string> = {
  Marrakech: 'RAK',
  Casablanca: 'CMN',
  Fès: 'FEZ',
  Fes: 'FEZ',
  Agadir: 'AGA',
  Rabat: 'RBA',
  Tanger: 'TNG',
  Nador: 'NDR',
  Essaouira: 'ESU',
  Dakhla: 'VIL',
  Oujda: 'OUD',
  Ouarzazate: 'OZZ',
  Tétouan: 'TTU',
  'Al Hoceïma': 'AHU',
  Errachidia: 'ERH',
  Laâyoune: 'EUN',
};

const modelKey = (v: { brand?: unknown; subBrand?: unknown }) =>
  `${idOf(v.brand) || String(v.brand)}|${idOf(v.subBrand) || String(v.subBrand)}`;

/** Cities with a model count (cars-by-city is already one card per model; bikes are units). */
export function toCityCounts(rows: { _id: string; items: { brand?: unknown; subBrand?: unknown }[] }[]) {
  return rows
    .map((r) => ({ city: r._id, count: new Set(r.items.map(modelKey)).size }))
    .filter((r) => r.city && r.count > 0)
    .sort((a, b) => b.count - a.count || a.city.localeCompare(b.city, 'fr'));
}

export const toCities = (counts: { city: string; count: number }[]): City[] =>
  counts.map(({ city, count }) => ({
    id: city,
    name: city,
    caption: translate(ns.labels, 'city.vehicles', { count }),
  }));

/** Tile code by accent-free city name; an airport row («Casablanca - Aéroport Mohammed V») takes its city's code. */
const CODES = new Map(Object.entries(AIRPORT_CODES).map(([city, code]) => [cityKey(city), code]));
CODES.set('tangier', 'TNG');
const tileCode = (city: string) => {
  const base = city.split(' - ')[0] ?? city;
  return CODES.get(cityKey(base)) ?? base.slice(0, 3).toUpperCase();
};

export const toDestinations = (counts: { city: string; count: number }[]): Destination[] =>
  counts.map(({ city, count }) => ({
    city,
    vehicles: count,
    code: tileCode(city),
    ...destinationPhoto(city),
  }));
